import { EntityType } from '@/components/entities/entityType';
import { ToolCallItem } from '@/components/mcp/mcpToolCallChip';
import { findNestedField, parseJson } from '@/utils';
import { DetectedEntityInfo } from './types';

export const parseToolData = (raw: any): any => {
  if (!raw) return null;
  let parsed = parseJson(raw);
  if (!parsed && typeof raw === 'string') {
    parsed = raw;
  }
  if (parsed && typeof parsed === 'object') {
    if (parsed.result !== undefined) {
      const inner = parseJson(parsed.result);
      if (inner) return inner;
      return parsed.result;
    }
    if (Array.isArray(parsed.content) && parsed.content.length > 0 && parsed.content[0]?.text) {
      const inner = parseJson(parsed.content[0].text);
      if (inner) return inner;
      return parsed.content[0].text;
    }
  }
  return parsed;
};

export const inferEntityTypeFromTool = (toolName?: string): EntityType => {
  if (!toolName) return EntityType.pipelineInstance;
  const lower = toolName.toLowerCase();
  if (lower.includes('data_source') || lower.includes('datasource')) return EntityType.dataSourceInstance;
  if (lower.includes('agent')) return EntityType.agentInstance;
  if (lower.includes('user_app') || lower.includes('userapp')) return EntityType.userAppInstance;
  if (lower.includes('blueprint')) return EntityType.pipelineBlueprint;
  return EntityType.pipelineInstance;
};

export const extractItemsFromData = (data: any, toolName?: string): any[] => {
  if (!data) return [];
  if (typeof data === 'string') {
    const parsed = parseJson(data);
    if (parsed && typeof parsed === 'object' && parsed !== data) {
      return extractItemsFromData(parsed, toolName);
    }
    return [];
  }

  if (typeof data !== 'object') return [];

  let rawList: any[] = [];
  if (Array.isArray(data)) {
    rawList = data;
  } else if (Array.isArray(data.data)) {
    rawList = data.data;
  } else if (Array.isArray(data.items)) {
    rawList = data.items;
  } else if (Array.isArray(data.instances)) {
    rawList = data.instances;
  } else if (Array.isArray(data.definitions)) {
    rawList = data.definitions;
  } else if (Array.isArray(data.blueprints)) {
    rawList = data.blueprints;
  } else if (Array.isArray(data.pipelines)) {
    rawList = data.pipelines;
  } else if (Array.isArray(data.content) && data.content.length > 0 && data.content[0]?.text) {
    const text = data.content[0].text;
    if (typeof text === 'string') {
      const parsed = parseJson(text);
      if (parsed && typeof parsed === 'object' && parsed !== data) {
        return extractItemsFromData(parsed, toolName);
      }
    }
  } else if (data.id || data.defId || data.pipelineId || data.dataSourceDefId || data.agentDefId) {
    rawList = [data];
  }

  const seen = new Set<string>();
  const items: any[] = [];

  for (const item of rawList) {
    if (!item || typeof item !== 'object') continue;
    const key = item.id || item.defId || item.pipelineId || item.dataSourceDefId || item.agentDefId || item.name;
    if (key && seen.has(String(key))) continue;
    if (key) seen.add(String(key));

    const cloned = { ...item };
    if (!cloned.entityType) {
      cloned.entityType = inferEntityTypeFromTool(toolName);
    }
    items.push(cloned);
  }

  return items;
};

/**
 * Extract entity context from tool calls and assistant response content.
 * Supports pipelines, data sources, agents, user apps, and entity lists.
 * Fully deduplicates detected entities and list items.
 */
export const extractEntityFromResponse = (toolCalls?: ToolCallItem[], content?: string): DetectedEntityInfo[] => {
  const detectedEntities: DetectedEntityInfo[] = [];
  const allItemList: any[] = [];
  const seenEntityKeys = new Set<string>();
  const seenItemKeys = new Set<string>();

  const addEntity = (entity: DetectedEntityInfo) => {
    const key = `${entity.type}:${entity.entityType || ''}:${entity.id || entity.name}`;
    if (seenEntityKeys.has(key)) return;
    seenEntityKeys.add(key);

    if (entity.type === 'entity_list' && Array.isArray(entity.items)) {
      const itemSeen = new Set<string>();
      const uniqueItems = entity.items.filter((item: any) => {
        const itemKey = item?.id || item?.name || (typeof item === 'object' ? JSON.stringify(item) : String(item));
        if (!itemKey || itemSeen.has(String(itemKey))) return false;
        itemSeen.add(String(itemKey));
        return true;
      });
      detectedEntities.push({ ...entity, items: uniqueItems });
    } else {
      detectedEntities.push(entity);
    }
  };

  if (toolCalls && toolCalls.length > 0) {
    for (const tc of toolCalls) {
      const name = tc.name?.toLowerCase() || '';
      const args = parseToolData(tc.arguments);
      const res = parseToolData(tc.result);

      // Pipeline Step Execution / Plan Tools
      if (
        name.includes('execute_pipeline_step') ||
        name.includes('get_execution_plan') ||
        name.includes('get_step_runs') ||
        name.includes('start_datasource') ||
        name.includes('stop_datasource') ||
        name.includes('upload_document_to_datasource') ||
        name.includes('execute_agent')
      ) {
        const pipelineId =
          args?.pipelineInstanceId || args?.pipelineId || res?.pipelineInstanceId || res?.pipelineId || res?.id;
        if (pipelineId) {
          const isError = Boolean(
            tc.isError ||
              res?.isError ||
              res?.error ||
              res?.status === 'failed' ||
              res?.success === false ||
              (typeof tc.result === 'string' && tc.result.toLowerCase().startsWith('failed')),
          );
          const errorMsg =
            res?.error ||
            res?.message ||
            (res?.content && res.content[0]?.text) ||
            (typeof tc.result === 'string' && tc.isError ? tc.result : undefined);

          addEntity({
            type: 'pipeline_action',
            entityType: EntityType.pipelineInstance,
            id: pipelineId,
            name: args?.pipelineName || res?.pipelineName,
            data: {
              ...(typeof args === 'object' ? args : {}),
              ...(typeof res === 'object' ? res : {}),
              isError,
              error: isError ? errorMsg || 'Step execution failed' : undefined,
              stepId: args?.stepId || res?.stepId,
              status: isError ? 'failed' : res?.status || 'completed',
            },
          });
          continue;
        }
      }

      const itemList = extractItemsFromData(res, name);

      if (itemList.length === 1 && itemList[0].id) {
        addEntity({
          type: 'single',
          entityType: itemList[0].entityType || inferEntityTypeFromTool(name),
          id: itemList[0].id,
          name: itemList[0].name,
          data: res || args,
        });
      } else if (itemList.length > 1) {
        const pipelineId =
          findNestedField(res, ['pipelineInstanceId', 'pipelineId']) ||
          findNestedField(args, ['pipelineInstanceId', 'pipelineId']);
        const pipelineName =
          findNestedField(res, ['pipelineName', 'pipelineInstanceName', 'name']) ||
          findNestedField(args, ['pipelineName', 'pipelineInstanceName', 'name']);

        if (pipelineId) {
          const matchPipeline = itemList.find((it) => it.id === pipelineId || it.name === pipelineName);
          if (matchPipeline) {
            addEntity({
              type: 'single',
              entityType: matchPipeline.entityType || EntityType.pipelineInstance,
              id: matchPipeline.id,
              name: matchPipeline.name,
              data: res || args,
            });
          }
        }

        const dataSourceId =
          findNestedField(res, ['dataSourceInstanceId', 'dataSourceId', 'dataSourceDefId']) ||
          findNestedField(args, ['dataSourceInstanceId', 'dataSourceId', 'dataSourceDefId']);
        const dataSourceName =
          findNestedField(res, ['dataSourceName', 'dataSourceInstanceName', 'name']) ||
          findNestedField(args, ['dataSourceName', 'dataSourceInstanceName', 'name']);

        if (dataSourceId) {
          const matchDataSource = itemList.find((it) => it.id === dataSourceId || it.name === dataSourceName);
          if (matchDataSource) {
            addEntity({
              type: 'single',
              entityType: matchDataSource.entityType || EntityType.dataSourceInstance,
              id: matchDataSource.id,
              name: matchDataSource.name || matchDataSource.id,
              data: res || args,
            });
          }
        }

        const agentId =
          findNestedField(res, ['agentInstanceId', 'agentId', 'agentDefId']) ||
          findNestedField(args, ['agentInstanceId', 'agentId', 'agentDefId']);
        const agentName =
          findNestedField(res, ['agentName', 'agentInstanceName', 'name']) ||
          findNestedField(args, ['agentName', 'agentInstanceName', 'name']);

        if (agentId) {
          const matchAgent = itemList.find((it) => it.id === agentId || it.name === agentName);
          if (matchAgent) {
            addEntity({
              type: 'single',
              entityType: matchAgent.entityType || EntityType.agentInstance,
              id: matchAgent.id,
              name: matchAgent.name || matchAgent.id,
              data: res || args,
            });
          }
        }

        const userAppId =
          findNestedField(res, ['userAppInstanceId', 'userAppId', 'userAppDefId']) ||
          findNestedField(args, ['userAppInstanceId', 'userAppId', 'userAppDefId']);
        const userAppName =
          findNestedField(res, ['userAppName', 'userAppInstanceName', 'name']) ||
          findNestedField(args, ['userAppName', 'userAppInstanceName', 'name']);

        if (userAppId) {
          const matchUserApp = itemList.find((it) => it.id === userAppId || it.name === userAppName);
          if (matchUserApp) {
            addEntity({
              type: 'single',
              entityType: matchUserApp.entityType || EntityType.userAppInstance,
              id: matchUserApp.id,
              name: matchUserApp.name || matchUserApp.id,
              data: res || args,
            });
          }
        }
      }

      for (const it of itemList) {
        const itemKey = it?.id || it?.name;
        if (itemKey && !seenItemKeys.has(String(itemKey))) {
          seenItemKeys.add(String(itemKey));
          allItemList.push(it);
        } else if (!itemKey) {
          allItemList.push(it);
        }
      }
    }

    if (detectedEntities.length === 0 && allItemList.length > 0) {
      let matchedItem: any = null;

      if (content && typeof content === 'string' && content.trim().length > 0) {
        const lowerContent = content.toLowerCase();
        const validItems: any[] = allItemList.filter((it: any) =>
          Boolean(it && it.name && typeof it.name === 'string'),
        );
        const sortedItems = validItems.sort((a: any, b: any) => String(b.name).length - String(a.name).length);

        for (const item of sortedItems) {
          const lowerName = String(item.name).toLowerCase().trim();
          if (lowerName.length > 2 && lowerContent.includes(lowerName)) {
            matchedItem = item;
            break;
          }
        }
      }

      if (matchedItem) {
        addEntity({
          type: 'single',
          entityType: matchedItem.entityType,
          id: matchedItem.id,
          name: matchedItem.name,
          data: matchedItem,
        });
      } else {
        addEntity({
          type: 'entity_list',
          entityType: allItemList[0].entityType || EntityType.pipelineInstance,
          title: 'Available Components',
          items: allItemList,
        });
      }
    }
  }

  if (detectedEntities.length === 0 && content && typeof content === 'string' && content.trim().length > 0) {
    const lowerContent = content.toLowerCase();
    const validItems: any[] = allItemList.filter((it: any) => Boolean(it && it.name && typeof it.name === 'string'));
    const sortedItems = validItems.sort((a: any, b: any) => String(b.name).length - String(a.name).length);

    for (const item of sortedItems) {
      const lowerName = String(item.name).toLowerCase().trim();
      if (lowerName.length > 2 && lowerContent.includes(lowerName)) {
        addEntity({
          type: 'single',
          entityType: item.entityType,
          id: item.id,
          name: item.name,
          data: item,
        });
        break;
      }
    }
  }

  return detectedEntities;
};
